import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { Navbar } from "../components/Navbar";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Slider } from "../components/ui/slider";
import { toast } from "sonner";
import { API_URL } from "../config/api";
import {
  TrendingUp,
  TrendingDown,
  Wallet,
  Clock,
  ArrowUpRight,
  ArrowDownRight,
  AlertTriangle,
  CheckCircle,
  XCircle,
  RefreshCw,
  BarChart3,
  PieChart,
  Activity,
  DollarSign,
  Gauge,
  Building2,
  Settings,
  Calendar,
  FileText,
  Zap,
  Play,
  Users,
  AlertOctagon,
  ChevronRight,
  Landmark,
  Bell,
  Shield,
  Target,
  Wrench,
  Info,
} from "lucide-react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  Legend,
  ReferenceLine,
  PieChart as RechartsPie,
  Pie,
  Cell,
} from "recharts";


// Format utilities
const formatCurrency = (amount) => {
  if (amount === null || amount === undefined) return "€0";
  return new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

const formatCurrencyK = (amount) => {
  if (Math.abs(amount) >= 1000000) {
    return `€${(amount / 1000000).toFixed(1)}M`;
  }
  if (Math.abs(amount) >= 1000) {
    return `€${(amount / 1000).toFixed(0)}k`;
  }
  return formatCurrency(amount);
};

const CHART_COLORS = ["#10b981", "#f97316", "#3b82f6", "#8b5cf6", "#ec4899", "#f59e0b"];

export default function CashFlowDashboardPage() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("dashboard");
  const [scenario, setScenario] = useState("base");
  
  // Data states
  const [dashboardData, setDashboardData] = useState(null);
  const [workingCapitalData, setWorkingCapitalData] = useState(null);
  const [monthlyData, setMonthlyData] = useState(null);
  const [simulationData, setSimulationData] = useState(null);
  const [multiCompanyData, setMultiCompanyData] = useState(null);
  const [alarmsData, setAlarmsData] = useState(null);
  const [selectedAlarm, setSelectedAlarm] = useState(null);
  
  // Vertical selection
  const [selectedVerticals, setSelectedVerticals] = useState([]);
  const [verticals, setVerticals] = useState([]);
  
  // SPA Modal
  const [showSPAModal, setShowSPAModal] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState(null);
  
  // Custom scenario params
  const [customParams, setCustomParams] = useState({
    dso: 0,
    dpo: 0,
    growth: 0,
    collectionRate: 95,
    costChange: 0
  });

  // Fetch main dashboard
  const fetchDashboard = useCallback(async () => {
    try {
      setLoading(true);
      const verticalParam = selectedVerticals.length > 0 ? selectedVerticals.join(",") : "";
      const response = await axios.get(
        `${API_URL}/cashflow-projection/dashboard?verticals=${verticalParam}&scenario=${scenario}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDashboardData(response.data);
      setVerticals(response.data.verticals_config || []);
      
      // Initialize selected verticals if empty
      if (selectedVerticals.length === 0 && response.data.verticals_config) {
        setSelectedVerticals(response.data.verticals_config.map(v => v.id));
      }
    } catch (error) {
      console.error("Failed to fetch dashboard:", error);
      toast.error("Failed to load cash flow data");
    } finally {
      setLoading(false);
    }
  }, [token, scenario, selectedVerticals]);

  // Fetch working capital
  const fetchWorkingCapital = useCallback(async () => {
    try {
      const verticalParam = selectedVerticals.length > 0 ? selectedVerticals.join(",") : "";
      const response = await axios.get(
        `${API_URL}/cashflow-projection/working-capital?verticals=${verticalParam}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setWorkingCapitalData(response.data);
    } catch (error) {
      console.error("Failed to fetch working capital:", error);
    }
  }, [token, selectedVerticals]);

  // Fetch monthly breakdown
  const fetchMonthly = useCallback(async () => {
    try {
      const verticalParam = selectedVerticals.length > 0 ? selectedVerticals.join(",") : "";
      const response = await axios.get(
        `${API_URL}/cashflow-projection/monthly-breakdown?verticals=${verticalParam}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMonthlyData(response.data);
    } catch (error) {
      console.error("Failed to fetch monthly data:", error);
    }
  }, [token, selectedVerticals]);

  // Fetch simulation
  const fetchSimulation = useCallback(async (customScenario = null) => {
    try {
      const verticalParam = selectedVerticals.length > 0 ? selectedVerticals.join(",") : "";
      let url = `${API_URL}/cashflow-projection/scenario-simulation?verticals=${verticalParam}&scenario=${customScenario || scenario}`;
      
      if (customScenario === "custom") {
        url += `&custom_dso=${customParams.dso}&custom_dpo=${customParams.dpo}&custom_growth=${customParams.growth}&custom_collection_rate=${customParams.collectionRate / 100}&custom_cost_change=${customParams.costChange}`;
      }
      
      const response = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
      setSimulationData(response.data);
    } catch (error) {
      console.error("Failed to fetch simulation:", error);
    }
  }, [token, selectedVerticals, scenario, customParams]);

  // Fetch multi-company
  const fetchMultiCompany = useCallback(async () => {
    try {
      const response = await axios.get(
        `${API_URL}/cashflow-projection/multi-company`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMultiCompanyData(response.data);
    } catch (error) {
      console.error("Failed to fetch multi-company:", error);
    }
  }, [token]);

  // Fetch alarms
  const fetchAlarms = useCallback(async () => {
    try {
      const verticalParam = selectedVerticals.length > 0 ? selectedVerticals.join(",") : "";
      const response = await axios.get(
        `${API_URL}/cashflow-projection/alarms?verticals=${verticalParam}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAlarmsData(response.data);
    } catch (error) {
      console.error("Failed to fetch alarms:", error);
    }
  }, [token, selectedVerticals]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  useEffect(() => {
    if (activeTab === "working-capital") fetchWorkingCapital();
    if (activeTab === "monthly") fetchMonthly();
    if (activeTab === "simulation") fetchSimulation();
    if (activeTab === "multi-company") fetchMultiCompany();
    if (activeTab === "alarms") fetchAlarms();
  }, [activeTab, fetchWorkingCapital, fetchMonthly, fetchSimulation, fetchMultiCompany, fetchAlarms]);

  // Toggle vertical selection
  const toggleVertical = (verticalId) => {
    setSelectedVerticals(prev => {
      if (prev.includes(verticalId)) {
        return prev.filter(v => v !== verticalId);
      } else {
        return [...prev, verticalId];
      }
    });
  };

  // Get status styling
  const getStatusColor = (status) => {
    switch (status) {
      case "healthy": return "bg-emerald-500/20 text-emerald-400 border-emerald-500/30";
      case "moderate": return "bg-amber-500/20 text-amber-400 border-amber-500/30";
      case "critical": return "bg-red-500/20 text-red-400 border-red-500/30";
      default: return "bg-slate-500/20 text-slate-400 border-slate-500/30";
    }
  };

  const getStatusDot = (status) => {
    switch (status) {
      case "healthy": return "bg-emerald-500";
      case "moderate": return "bg-amber-500";
      case "critical": return "bg-red-500";
      default: return "bg-slate-500";
    }
  };

  if (loading && !dashboardData) {
    return (
      <div className="flex items-center justify-center h-screen bg-slate-950">
        <RefreshCw className="h-8 w-8 animate-spin text-cyan-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950">
      <Navbar />
      
      {/* Header */}
      <header className="sticky top-16 z-10 bg-slate-900/95 backdrop-blur-sm border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-lg">
                <Activity className="h-5 w-5 text-white" />
              </div>
              <div>
                <h1 className="text-lg sm:text-xl font-bold text-white">Cash Flow Pro</h1>
                <p className="text-xs text-slate-400 hidden sm:block">Real-time liquidity management</p>
              </div>
            </div>
            
            <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
              {/* Company Filter */}
              <div className="flex items-center gap-1 flex-wrap">
                {verticals.map((v) => (
                  <button
                    key={v.id}
                    onClick={() => toggleVertical(v.id)}
                    className={`px-2 py-1 text-xs rounded-full border transition-all ${
                      selectedVerticals.includes(v.id)
                        ? "bg-white/10 border-white/30 text-white"
                        : "bg-transparent border-slate-700 text-slate-500"
                    }`}
                    style={selectedVerticals.includes(v.id) ? { borderColor: v.color } : {}}
                  >
                    {v.name}
                  </button>
                ))}
              </div>
              
              <Button
                size="sm"
                variant="ghost"
                onClick={fetchDashboard}
                className="text-slate-400 hover:text-white"
                disabled={loading}
              >
                <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-4 sm:mb-6 bg-slate-900/50 border border-slate-800 flex-wrap h-auto gap-1 p-1">
            <TabsTrigger value="dashboard" className="data-[state=active]:bg-cyan-600 gap-1.5 text-xs sm:text-sm">
              <Gauge className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Dashboard</span>
              <span className="sm:hidden">Home</span>
            </TabsTrigger>
            <TabsTrigger value="working-capital" className="data-[state=active]:bg-cyan-600 gap-1.5 text-xs sm:text-sm">
              <BarChart3 className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Working Capital</span>
              <span className="sm:hidden">WC</span>
            </TabsTrigger>
            <TabsTrigger value="monthly" className="data-[state=active]:bg-cyan-600 gap-1.5 text-xs sm:text-sm">
              <Calendar className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Monthly</span>
              <span className="sm:hidden">Month</span>
            </TabsTrigger>
            <TabsTrigger value="simulation" className="data-[state=active]:bg-cyan-600 gap-1.5 text-xs sm:text-sm">
              <Play className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Scenarios</span>
              <span className="sm:hidden">Sim</span>
            </TabsTrigger>
            <TabsTrigger value="multi-company" className="data-[state=active]:bg-cyan-600 gap-1.5 text-xs sm:text-sm">
              <Building2 className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Multi-Company</span>
              <span className="sm:hidden">Group</span>
            </TabsTrigger>
            <TabsTrigger value="alarms" className="data-[state=active]:bg-red-600 gap-1.5 text-xs sm:text-sm relative">
              <Bell className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Alarms</span>
              <span className="sm:hidden">Alert</span>
              {alarmsData?.summary?.red_count > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full text-[10px] flex items-center justify-center text-white font-bold">
                  {alarmsData.summary.red_count}
                </span>
              )}
            </TabsTrigger>
          </TabsList>

          {/* ==================== MAIN DASHBOARD ==================== */}
          <TabsContent value="dashboard" className="space-y-4 sm:space-y-6">
            {dashboardData && (
              <>
                {/* KPI Cards */}
                <div className="grid grid-cols-3 gap-3 sm:gap-4">
                  {/* Cash Today */}
                  <Card className="bg-slate-900/50 border-slate-800">
                    <CardContent className="p-3 sm:p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-slate-400 text-xs sm:text-sm flex items-center gap-1.5">
                          <Wallet className="h-3.5 w-3.5 text-cyan-400" />
                          <span className="hidden sm:inline">CASH TODAY</span>
                          <span className="sm:hidden">CASH</span>
                        </span>
                        <span className={`w-2 h-2 rounded-full ${getStatusDot(dashboardData.cash_today?.status)}`} />
                      </div>
                      <div className="text-xl sm:text-3xl font-bold text-white">
                        {formatCurrencyK(dashboardData.cash_today?.value || 0)}
                      </div>
                      <Badge className={`mt-2 text-xs ${getStatusColor(dashboardData.cash_today?.status)}`}>
                        {dashboardData.cash_today?.status === "healthy" ? "Stable" : 
                         dashboardData.cash_today?.status === "moderate" ? "Monitor" : "Low"}
                      </Badge>
                    </CardContent>
                  </Card>

                  {/* 30D Forecast */}
                  <Card className="bg-slate-900/50 border-slate-800">
                    <CardContent className="p-3 sm:p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-slate-400 text-xs sm:text-sm flex items-center gap-1.5">
                          <TrendingUp className="h-3.5 w-3.5 text-emerald-400" />
                          <span className="hidden sm:inline">30D FORECAST</span>
                          <span className="sm:hidden">30D</span>
                        </span>
                        <span className={`w-2 h-2 rounded-full ${getStatusDot(dashboardData.forecast_30d?.status)}`} />
                      </div>
                      <div className={`text-xl sm:text-3xl font-bold ${
                        (dashboardData.forecast_30d?.value || 0) >= 0 ? "text-emerald-400" : "text-red-400"
                      }`}>
                        {(dashboardData.forecast_30d?.value || 0) >= 0 ? "+" : ""}
                        {formatCurrencyK(dashboardData.forecast_30d?.value || 0)}
                      </div>
                      <Badge className={`mt-2 text-xs ${getStatusColor(dashboardData.forecast_30d?.status)}`}>
                        {dashboardData.forecast_30d?.status === "healthy" ? "Growing" : 
                         dashboardData.forecast_30d?.status === "moderate" ? "Volatile" : "Declining"}
                      </Badge>
                    </CardContent>
                  </Card>

                  {/* Runway */}
                  <Card className="bg-slate-900/50 border-slate-800">
                    <CardContent className="p-3 sm:p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-slate-400 text-xs sm:text-sm flex items-center gap-1.5">
                          <Clock className="h-3.5 w-3.5 text-purple-400" />
                          RUNWAY
                        </span>
                        <span className={`w-2 h-2 rounded-full ${getStatusDot(dashboardData.runway?.status)}`} />
                      </div>
                      <div className="text-xl sm:text-3xl font-bold text-white">
                        {dashboardData.runway?.value || 0}
                        <span className="text-sm sm:text-lg text-slate-400 ml-1">mo</span>
                      </div>
                      <Badge className={`mt-2 text-xs ${getStatusColor(dashboardData.runway?.status)}`}>
                        {dashboardData.runway?.status === "healthy" ? "Safe" : 
                         dashboardData.runway?.status === "moderate" ? "Moderate" : "Critical"}
                      </Badge>
                    </CardContent>
                  </Card>
                </div>

                {/* 180-Day Forecast Chart */}
                <Card className="bg-slate-900/50 border-slate-800">
                  <CardHeader className="pb-2">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                      <CardTitle className="text-white flex items-center gap-2 text-base sm:text-lg">
                        <BarChart3 className="h-5 w-5 text-cyan-400" />
                        Cash Balance – 180 Day Forecast
                      </CardTitle>
                      <Select value={scenario} onValueChange={setScenario}>
                        <SelectTrigger className="w-[140px] sm:w-[180px] bg-slate-800 border-slate-700 text-white text-xs sm:text-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-800 border-slate-700">
                          {dashboardData.available_scenarios?.map((s) => (
                            <SelectItem key={s.id} value={s.id} className="text-white hover:bg-slate-700">
                              {s.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="h-[200px] sm:h-[280px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={dashboardData.projection?.daily || []}>
                          <defs>
                            <linearGradient id="colorBalance" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#06b6d4" stopOpacity={0.3}/>
                              <stop offset="95%" stopColor="#06b6d4" stopOpacity={0}/>
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                          <XAxis 
                            dataKey="day" 
                            stroke="#64748b"
                            tick={{ fontSize: 10 }}
                            tickFormatter={(day) => `D${day}`}
                          />
                          <YAxis 
                            stroke="#64748b"
                            tick={{ fontSize: 10 }}
                            tickFormatter={(val) => formatCurrencyK(val)}
                            width={50}
                          />
                          <Tooltip
                            contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: '8px' }}
                            labelStyle={{ color: '#fff' }}
                            formatter={(value) => [formatCurrency(value), 'Balance']}
                            labelFormatter={(day) => `Day ${day}`}
                          />
                          <ReferenceLine 
                            y={dashboardData.projection?.min_threshold || 0} 
                            stroke="#ef4444" 
                            strokeDasharray="5 5"
                          />
                          <Area
                            type="monotone"
                            dataKey="balance"
                            stroke="#06b6d4"
                            strokeWidth={2}
                            fillOpacity={1}
                            fill="url(#colorBalance)"
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                    
                    {/* Milestones */}
                    <div className="grid grid-cols-4 sm:grid-cols-5 gap-2 sm:gap-3 mt-4">
                      {["day_0", "day_30", "day_60", "day_90", "day_180"].map((key, i) => (
                        <div key={key} className={`bg-slate-800/50 rounded-lg p-2 sm:p-3 text-center ${i === 4 ? "hidden sm:block" : ""}`}>
                          <p className="text-slate-400 text-xs">
                            {key === "day_0" ? "Today" : `+${key.split("_")[1]}d`}
                          </p>
                          <p className="text-white font-semibold text-sm sm:text-base">
                            {formatCurrencyK(dashboardData.projection?.milestones?.[key] || 0)}
                          </p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Alerts */}
                <Card className="bg-slate-900/50 border-slate-800">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-white text-base sm:text-lg flex items-center gap-2">
                      <AlertTriangle className="h-5 w-5 text-amber-400" />
                      Alerts
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {dashboardData.alerts?.map((alert, i) => (
                        <div 
                          key={i}
                          className={`flex items-center gap-3 p-2 sm:p-3 rounded-lg ${
                            alert.type === "danger" ? "bg-red-500/10 border border-red-500/30" :
                            alert.type === "warning" ? "bg-amber-500/10 border border-amber-500/30" :
                            "bg-emerald-500/10 border border-emerald-500/30"
                          }`}
                        >
                          {alert.type === "danger" && <XCircle className="h-4 w-4 text-red-400 flex-shrink-0" />}
                          {alert.type === "warning" && <AlertTriangle className="h-4 w-4 text-amber-400 flex-shrink-0" />}
                          {alert.type === "success" && <CheckCircle className="h-4 w-4 text-emerald-400 flex-shrink-0" />}
                          <span className={`text-xs sm:text-sm ${
                            alert.type === "danger" ? "text-red-300" :
                            alert.type === "warning" ? "text-amber-300" :
                            "text-emerald-300"
                          }`}>
                            {alert.message}
                          </span>
                        </div>
                      ))}
                      {(!dashboardData.alerts || dashboardData.alerts.length === 0) && (
                        <div className="text-slate-500 text-sm text-center py-4">No alerts</div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </>
            )}
          </TabsContent>

          {/* ==================== WORKING CAPITAL ==================== */}
          <TabsContent value="working-capital" className="space-y-4 sm:space-y-6">
            {workingCapitalData && (
              <>
                {/* AR / AP Summary */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {/* AR Card */}
                  <Card className="bg-slate-900/50 border-slate-800">
                    <CardHeader className="pb-2">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-white text-base flex items-center gap-2">
                          <ArrowUpRight className="h-5 w-5 text-emerald-400" />
                          Accounts Receivable
                        </CardTitle>
                        <Badge className="bg-emerald-600">DSO: {workingCapitalData.ar?.dso || 0}d</Badge>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl sm:text-3xl font-bold text-emerald-400 mb-4">
                        {formatCurrency(workingCapitalData.ar?.total || 0)}
                      </div>
                      
                      {/* Aging Bars */}
                      <div className="space-y-3">
                        {[
                          { key: "0_30", label: "0-30d", color: "bg-emerald-500" },
                          { key: "30_60", label: "30-60d", color: "bg-amber-500" },
                          { key: "60_90", label: "60-90d", color: "bg-orange-500" },
                          { key: "90_plus", label: "90+d", color: "bg-red-500" },
                          { key: "overdue", label: "Overdue", color: "bg-red-700" },
                        ].map((bucket) => {
                          const amount = workingCapitalData.ar?.aging?.[bucket.key]?.amount || 0;
                          const total = workingCapitalData.ar?.total || 1;
                          const pct = Math.round((amount / total) * 100);
                          return (
                            <div key={bucket.key}>
                              <div className="flex justify-between text-xs sm:text-sm mb-1">
                                <span className="text-slate-400">{bucket.label}</span>
                                <span className="text-white">{formatCurrencyK(amount)}</span>
                              </div>
                              <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                                <div 
                                  className={`h-full ${bucket.color} rounded-full`}
                                  style={{ width: `${pct}%` }}
                                />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>

                  {/* AP Card */}
                  <Card className="bg-slate-900/50 border-slate-800">
                    <CardHeader className="pb-2">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-white text-base flex items-center gap-2">
                          <ArrowDownRight className="h-5 w-5 text-orange-400" />
                          Accounts Payable
                        </CardTitle>
                        <Badge className="bg-orange-600">DPO: {workingCapitalData.ap?.dpo || 0}d</Badge>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl sm:text-3xl font-bold text-orange-400 mb-4">
                        {formatCurrency(workingCapitalData.ap?.total || 0)}
                      </div>
                      
                      {/* Aging Bars */}
                      <div className="space-y-3">
                        {[
                          { key: "0_30", label: "0-30d", color: "bg-emerald-500" },
                          { key: "30_60", label: "30-60d", color: "bg-amber-500" },
                          { key: "60_90", label: "60-90d", color: "bg-orange-500" },
                          { key: "90_plus", label: "90+d", color: "bg-red-500" },
                          { key: "overdue", label: "Overdue", color: "bg-red-700" },
                        ].map((bucket) => {
                          const amount = workingCapitalData.ap?.aging?.[bucket.key]?.amount || 0;
                          const total = workingCapitalData.ap?.total || 1;
                          const pct = Math.round((amount / total) * 100);
                          return (
                            <div key={bucket.key}>
                              <div className="flex justify-between text-xs sm:text-sm mb-1">
                                <span className="text-slate-400">{bucket.label}</span>
                                <span className="text-white">{formatCurrencyK(amount)}</span>
                              </div>
                              <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                                <div 
                                  className={`h-full ${bucket.color} rounded-full`}
                                  style={{ width: `${pct}%` }}
                                />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Metrics */}
                <Card className="bg-slate-900/50 border-slate-800">
                  <CardContent className="p-4 sm:p-6">
                    <div className="grid grid-cols-3 gap-4 sm:gap-6 text-center">
                      <div>
                        <p className="text-slate-400 text-xs sm:text-sm mb-1">Cash Conversion Cycle</p>
                        <p className={`text-2xl sm:text-3xl font-bold ${
                          (workingCapitalData.metrics?.cash_conversion_cycle || 0) > 0 ? "text-amber-400" : "text-emerald-400"
                        }`}>
                          {workingCapitalData.metrics?.cash_conversion_cycle || 0}
                          <span className="text-sm sm:text-lg text-slate-400 ml-1">days</span>
                        </p>
                      </div>
                      <div>
                        <p className="text-slate-400 text-xs sm:text-sm mb-1">Net Working Capital</p>
                        <p className={`text-2xl sm:text-3xl font-bold ${
                          (workingCapitalData.metrics?.net_working_capital || 0) >= 0 ? "text-emerald-400" : "text-red-400"
                        }`}>
                          {(workingCapitalData.metrics?.net_working_capital || 0) >= 0 ? "+" : ""}
                          {formatCurrencyK(workingCapitalData.metrics?.net_working_capital || 0)}
                        </p>
                      </div>
                      <div>
                        <p className="text-slate-400 text-xs sm:text-sm mb-1">Trend</p>
                        <div className="flex items-center justify-center gap-2">
                          <TrendingUp className="h-6 w-6 text-emerald-400" />
                          <span className="text-emerald-400 text-sm sm:text-base">Improving</span>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Top Vendors for SPA */}
                <Card className="bg-slate-900/50 border-slate-800">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-white text-base flex items-center gap-2">
                      <Users className="h-5 w-5 text-purple-400" />
                      Top Vendors (High AP)
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {workingCapitalData.ap?.top_vendors?.slice(0, 5).map((vendor, i) => (
                        <div 
                          key={i}
                          className="flex items-center justify-between p-2 sm:p-3 bg-slate-800/50 rounded-lg hover:bg-slate-800 cursor-pointer"
                          onClick={() => {
                            setSelectedVendor(vendor);
                            setShowSPAModal(true);
                          }}
                        >
                          <div>
                            <p className="text-white text-sm">{vendor.name}</p>
                            {vendor.overdue > 0 && (
                              <p className="text-red-400 text-xs">€{vendor.overdue.toLocaleString()} overdue</p>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-white font-medium">{formatCurrencyK(vendor.total)}</span>
                            <ChevronRight className="h-4 w-4 text-slate-500" />
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </>
            )}
          </TabsContent>

          {/* ==================== MONTHLY BREAKDOWN ==================== */}
          <TabsContent value="monthly" className="space-y-4 sm:space-y-6">
            {monthlyData && (
              <>
                <Card className="bg-slate-900/50 border-slate-800">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-white text-lg sm:text-xl">
                      {monthlyData.month_name}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      {/* Inflows/Outflows */}
                      <div className="space-y-6">
                        {/* Inflows */}
                        <div>
                          <h4 className="text-emerald-400 font-semibold mb-3 flex items-center gap-2">
                            <ArrowUpRight className="h-4 w-4" />
                            INFLOWS
                          </h4>
                          <div className="space-y-2">
                            <div className="flex justify-between py-2 border-b border-slate-700">
                              <span className="text-slate-300 text-sm">Customer Collections</span>
                              <span className="text-emerald-400">{formatCurrency(monthlyData.inflows?.customer_collections || 0)}</span>
                            </div>
                            <div className="flex justify-between py-2 border-b border-slate-700">
                              <span className="text-slate-300 text-sm">New Sales (advance)</span>
                              <span className="text-emerald-400">{formatCurrency(monthlyData.inflows?.new_sales_advance || 0)}</span>
                            </div>
                            <div className="flex justify-between py-2 border-b border-slate-700">
                              <span className="text-slate-300 text-sm">Other</span>
                              <span className="text-emerald-400">{formatCurrency(monthlyData.inflows?.other || 0)}</span>
                            </div>
                            <div className="flex justify-between py-2 font-bold">
                              <span className="text-white">Total Inflows</span>
                              <span className="text-emerald-400 text-lg">{formatCurrency(monthlyData.inflows?.total || 0)}</span>
                            </div>
                          </div>
                        </div>

                        {/* Outflows */}
                        <div>
                          <h4 className="text-red-400 font-semibold mb-3 flex items-center gap-2">
                            <ArrowDownRight className="h-4 w-4" />
                            OUTFLOWS
                          </h4>
                          <div className="space-y-2">
                            {[
                              { key: "suppliers", label: "Suppliers" },
                              { key: "salaries", label: "Salaries" },
                              { key: "leasing", label: "Leasing / Vehicles" },
                              { key: "taxes", label: "Taxes" },
                              { key: "loan_repayment", label: "Loan Repayment" },
                            ].map((item) => (
                              <div key={item.key} className="flex justify-between py-2 border-b border-slate-700">
                                <span className="text-slate-300 text-sm">{item.label}</span>
                                <span className="text-red-400">-{formatCurrency(monthlyData.outflows?.[item.key] || 0)}</span>
                              </div>
                            ))}
                            <div className="flex justify-between py-2 font-bold">
                              <span className="text-white">Total Outflows</span>
                              <span className="text-red-400 text-lg">-{formatCurrency(monthlyData.outflows?.total || 0)}</span>
                            </div>
                          </div>
                        </div>

                        {/* Net */}
                        <div className={`p-4 rounded-lg ${
                          (monthlyData.net_change || 0) >= 0 
                            ? "bg-emerald-500/10 border border-emerald-500/30" 
                            : "bg-red-500/10 border border-red-500/30"
                        }`}>
                          <div className="flex justify-between items-center">
                            <span className="text-white font-semibold">Net Cash Change</span>
                            <span className={`text-xl sm:text-2xl font-bold ${
                              (monthlyData.net_change || 0) >= 0 ? "text-emerald-400" : "text-red-400"
                            }`}>
                              {(monthlyData.net_change || 0) >= 0 ? "+" : ""}
                              {formatCurrency(monthlyData.net_change || 0)}
                            </span>
                          </div>
                          <div className="flex justify-between items-center mt-2 pt-2 border-t border-slate-600">
                            <span className="text-slate-400 text-sm">Closing Balance</span>
                            <span className="text-white font-semibold">{formatCurrency(monthlyData.closing_balance || 0)}</span>
                          </div>
                        </div>
                      </div>

                      {/* Pie Chart */}
                      <div>
                        <h4 className="text-white font-semibold mb-4 text-center">Expense Distribution</h4>
                        <div className="h-[250px] sm:h-[300px]">
                          <ResponsiveContainer width="100%" height="100%">
                            <RechartsPie>
                              <Pie
                                data={Object.entries(monthlyData.outflow_percentages || {}).map(([key, value]) => ({
                                  name: key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' '),
                                  value
                                }))}
                                cx="50%"
                                cy="50%"
                                outerRadius={80}
                                fill="#8884d8"
                                dataKey="value"
                                label={({ name, value }) => `${name}: ${value}%`}
                                labelLine={false}
                              >
                                {Object.keys(monthlyData.outflow_percentages || {}).map((_, index) => (
                                  <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                                ))}
                              </Pie>
                              <Tooltip 
                                contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155' }}
                                formatter={(value) => [`${value}%`, 'Share']}
                              />
                            </RechartsPie>
                          </ResponsiveContainer>
                        </div>
                        {/* Legend */}
                        <div className="flex flex-wrap justify-center gap-3 mt-4">
                          {Object.keys(monthlyData.outflow_percentages || {}).map((key, i) => (
                            <div key={key} className="flex items-center gap-1.5">
                              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: CHART_COLORS[i % CHART_COLORS.length] }} />
                              <span className="text-slate-400 text-xs capitalize">{key.replace(/_/g, ' ')}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </>
            )}
          </TabsContent>

          {/* ==================== SCENARIO SIMULATION ==================== */}
          <TabsContent value="simulation" className="space-y-4 sm:space-y-6">
            {/* Scenario Toggle */}
            <div className="flex flex-wrap gap-2">
              {["base", "growth", "slow_collections", "crisis", "custom"].map((s) => (
                <Button
                  key={s}
                  variant={scenario === s ? "default" : "outline"}
                  size="sm"
                  onClick={() => {
                    setScenario(s);
                    fetchSimulation(s);
                  }}
                  className={scenario === s ? "bg-cyan-600" : "border-slate-700 text-slate-400"}
                >
                  {s === "base" && "Base Case"}
                  {s === "growth" && "Growth +20%"}
                  {s === "slow_collections" && "Slow Collections"}
                  {s === "crisis" && "Crisis Mode"}
                  {s === "custom" && "Custom"}
                </Button>
              ))}
            </div>

            {/* Custom Parameters */}
            {scenario === "custom" && (
              <Card className="bg-slate-900/50 border-slate-800">
                <CardHeader className="pb-2">
                  <CardTitle className="text-white text-base">Custom Parameters</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
                    <div>
                      <Label className="text-slate-400 text-xs">DSO Adjustment</Label>
                      <Input 
                        type="number" 
                        value={customParams.dso}
                        onChange={(e) => setCustomParams({...customParams, dso: parseInt(e.target.value) || 0})}
                        className="bg-slate-800 border-slate-700 text-white mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-400 text-xs">DPO Adjustment</Label>
                      <Input 
                        type="number" 
                        value={customParams.dpo}
                        onChange={(e) => setCustomParams({...customParams, dpo: parseInt(e.target.value) || 0})}
                        className="bg-slate-800 border-slate-700 text-white mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-400 text-xs">Revenue Growth %</Label>
                      <Input 
                        type="number" 
                        value={customParams.growth}
                        onChange={(e) => setCustomParams({...customParams, growth: parseFloat(e.target.value) || 0})}
                        className="bg-slate-800 border-slate-700 text-white mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-400 text-xs">Collection Rate %</Label>
                      <Input 
                        type="number" 
                        value={customParams.collectionRate}
                        onChange={(e) => setCustomParams({...customParams, collectionRate: parseFloat(e.target.value) || 95})}
                        className="bg-slate-800 border-slate-700 text-white mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-400 text-xs">Cost Change %</Label>
                      <Input 
                        type="number" 
                        value={customParams.costChange}
                        onChange={(e) => setCustomParams({...customParams, costChange: parseFloat(e.target.value) || 0})}
                        className="bg-slate-800 border-slate-700 text-white mt-1"
                      />
                    </div>
                  </div>
                  <Button 
                    className="mt-4 bg-cyan-600"
                    onClick={() => fetchSimulation("custom")}
                  >
                    Run Simulation
                  </Button>
                </CardContent>
              </Card>
            )}

            {simulationData && (
              <>
                {/* Impact Summary */}
                <Card className="bg-slate-900/50 border-slate-800">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-white text-base flex items-center gap-2">
                      <Zap className="h-5 w-5 text-amber-400" />
                      Impact Summary – {simulationData.scenario}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                      <div className="bg-slate-800/50 rounded-lg p-3">
                        <p className="text-slate-400 text-xs">Revenue Change</p>
                        <p className={`text-lg sm:text-xl font-bold ${(simulationData.impact?.revenue_change || 0) >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                          {(simulationData.impact?.revenue_change || 0) >= 0 ? "+" : ""}
                          {formatCurrencyK(simulationData.impact?.revenue_change || 0)}
                        </p>
                      </div>
                      <div className="bg-slate-800/50 rounded-lg p-3">
                        <p className="text-slate-400 text-xs">WC Need</p>
                        <p className="text-lg sm:text-xl font-bold text-amber-400">
                          +{formatCurrencyK(simulationData.impact?.working_capital_need || 0)}
                        </p>
                      </div>
                      <div className="bg-slate-800/50 rounded-lg p-3">
                        <p className="text-slate-400 text-xs">DSO Projected</p>
                        <p className="text-lg sm:text-xl font-bold text-white">
                          {simulationData.impact?.dso_projected || 0} days
                        </p>
                      </div>
                      <div className="bg-slate-800/50 rounded-lg p-3">
                        <p className="text-slate-400 text-xs">Cash Dip Month</p>
                        <p className={`text-lg sm:text-xl font-bold ${simulationData.impact?.cash_dip_month ? "text-red-400" : "text-emerald-400"}`}>
                          {simulationData.impact?.cash_dip_month ? `Month ${simulationData.impact.cash_dip_month}` : "None"}
                        </p>
                      </div>
                    </div>

                    {/* Minimum Liquidity */}
                    <div className={`mt-4 p-4 rounded-lg ${
                      (simulationData.impact?.minimum_liquidity || 0) < 0 
                        ? "bg-red-500/10 border border-red-500/30" 
                        : "bg-slate-800/50"
                    }`}>
                      <div className="flex justify-between items-center">
                        <span className="text-slate-300">Minimum Liquidity (Day {simulationData.impact?.minimum_liquidity_day})</span>
                        <span className={`text-xl font-bold ${
                          (simulationData.impact?.minimum_liquidity || 0) < 0 ? "text-red-400" : "text-white"
                        }`}>
                          {formatCurrency(simulationData.impact?.minimum_liquidity || 0)}
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Recommendations */}
                {simulationData.recommendations?.length > 0 && (
                  <Card className="bg-gradient-to-br from-cyan-900/30 to-blue-900/30 border-cyan-700/50">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-white text-base flex items-center gap-2">
                        <FileText className="h-5 w-5 text-cyan-400" />
                        Recommendations
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-2">
                        {simulationData.recommendations.map((rec, i) => (
                          <li key={i} className="flex items-start gap-2 text-cyan-100">
                            <span className="text-cyan-400 mt-0.5">•</span>
                            <span className="text-sm">{rec}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                )}

                {/* Projection Chart */}
                <Card className="bg-slate-900/50 border-slate-800">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-white text-base">Projected Cash Balance</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-[200px] sm:h-[250px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={simulationData.projection || []}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                          <XAxis 
                            dataKey="day" 
                            stroke="#64748b"
                            tick={{ fontSize: 10 }}
                            tickFormatter={(day) => `D${day}`}
                          />
                          <YAxis 
                            stroke="#64748b"
                            tick={{ fontSize: 10 }}
                            tickFormatter={(val) => formatCurrencyK(val)}
                          />
                          <Tooltip
                            contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155' }}
                            formatter={(value) => [formatCurrency(value), 'Balance']}
                          />
                          <ReferenceLine y={0} stroke="#ef4444" strokeDasharray="5 5" />
                          <Line 
                            type="monotone" 
                            dataKey="balance" 
                            stroke="#06b6d4" 
                            strokeWidth={2}
                            dot={false}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>
              </>
            )}
          </TabsContent>

          {/* ==================== MULTI-COMPANY VIEW ==================== */}
          <TabsContent value="multi-company" className="space-y-4 sm:space-y-6">
            {multiCompanyData && (
              <>
                <Card className="bg-slate-900/50 border-slate-800">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-white text-lg flex items-center gap-2">
                      <Building2 className="h-5 w-5 text-purple-400" />
                      Group Structure
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {/* Company Table */}
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-slate-700">
                            <th className="text-left py-3 px-2 text-slate-400 font-medium">Company</th>
                            <th className="text-right py-3 px-2 text-slate-400 font-medium">Cash</th>
                            <th className="text-right py-3 px-2 text-slate-400 font-medium">AR</th>
                            <th className="text-right py-3 px-2 text-slate-400 font-medium">AP</th>
                            <th className="text-center py-3 px-2 text-slate-400 font-medium">Risk</th>
                          </tr>
                        </thead>
                        <tbody>
                          {multiCompanyData.companies?.map((company) => (
                            <tr 
                              key={company.id} 
                              className="border-b border-slate-800 hover:bg-slate-800/50 cursor-pointer"
                              onClick={() => {
                                setSelectedVerticals([company.id]);
                                setActiveTab("dashboard");
                              }}
                            >
                              <td className="py-3 px-2">
                                <div className="flex items-center gap-2">
                                  <div className="w-2 h-2 rounded-full" style={{ backgroundColor: company.color }} />
                                  <span className="text-white">{company.name}</span>
                                </div>
                              </td>
                              <td className="py-3 px-2 text-right text-white font-medium">
                                {formatCurrencyK(company.cash)}
                              </td>
                              <td className="py-3 px-2 text-right text-emerald-400">
                                {formatCurrencyK(company.ar)}
                              </td>
                              <td className="py-3 px-2 text-right text-orange-400">
                                {formatCurrencyK(company.ap)}
                              </td>
                              <td className="py-3 px-2 text-center">
                                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${
                                  company.risk === "healthy" ? "bg-emerald-500/20 text-emerald-400" :
                                  company.risk === "moderate" ? "bg-amber-500/20 text-amber-400" :
                                  "bg-red-500/20 text-red-400"
                                }`}>
                                  <span className={`w-1.5 h-1.5 rounded-full ${getStatusDot(company.risk)}`} />
                                  {company.risk === "healthy" ? "OK" : company.risk === "moderate" ? "Watch" : "Alert"}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Group Totals */}
                    <div className="mt-6 pt-4 border-t border-slate-700">
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                        <div className="bg-slate-800/50 rounded-lg p-3 text-center">
                          <p className="text-slate-400 text-xs">Group Cash</p>
                          <p className="text-xl font-bold text-white">{formatCurrencyK(multiCompanyData.group_totals?.cash || 0)}</p>
                        </div>
                        <div className="bg-slate-800/50 rounded-lg p-3 text-center">
                          <p className="text-slate-400 text-xs">Net Working Capital</p>
                          <p className={`text-xl font-bold ${(multiCompanyData.group_totals?.net_wc || 0) >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                            {(multiCompanyData.group_totals?.net_wc || 0) >= 0 ? "+" : ""}
                            {formatCurrencyK(multiCompanyData.group_totals?.net_wc || 0)}
                          </p>
                        </div>
                        <div className="bg-slate-800/50 rounded-lg p-3 text-center col-span-2 sm:col-span-2">
                          <p className="text-slate-400 text-xs">Group Liquidity Risk</p>
                          <Badge className={`mt-1 ${getStatusColor(multiCompanyData.group_totals?.risk)}`}>
                            {multiCompanyData.group_totals?.risk === "healthy" ? "Low Risk" :
                             multiCompanyData.group_totals?.risk === "moderate" ? "Medium Risk" : "High Risk"}
                          </Badge>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Quick Actions */}
                <div className="flex flex-wrap gap-2">
                  <Button 
                    variant="outline" 
                    className="border-slate-700 text-slate-300"
                    onClick={() => {
                      setSelectedVerticals(multiCompanyData.companies.map(c => c.id));
                      setActiveTab("dashboard");
                    }}
                  >
                    <Landmark className="h-4 w-4 mr-2" />
                    View Consolidated
                  </Button>
                  <Button 
                    variant="outline" 
                    className="border-slate-700 text-slate-300"
                    onClick={() => setActiveTab("simulation")}
                  >
                    <Play className="h-4 w-4 mr-2" />
                    Simulate Consolidation
                  </Button>
                </div>
              </>
            )}
          </TabsContent>

          {/* ==================== ALARMS DASHBOARD ==================== */}
          <TabsContent value="alarms" className="space-y-4 sm:space-y-6">
            {alarmsData && (
              <>
                {/* Summary Header */}
                <div className={`p-4 rounded-lg border ${
                  alarmsData.summary?.overall_status === "red" 
                    ? "bg-red-500/10 border-red-500/30" 
                    : alarmsData.summary?.overall_status === "yellow"
                    ? "bg-amber-500/10 border-amber-500/30"
                    : "bg-emerald-500/10 border-emerald-500/30"
                }`}>
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <Shield className={`h-8 w-8 ${
                        alarmsData.summary?.overall_status === "red" ? "text-red-400" :
                        alarmsData.summary?.overall_status === "yellow" ? "text-amber-400" :
                        "text-emerald-400"
                      }`} />
                      <div>
                        <h2 className="text-lg font-bold text-white">Financial Health Monitor</h2>
                        <p className="text-sm text-slate-400">
                          {alarmsData.summary?.red_count > 0 
                            ? `${alarmsData.summary.red_count} critical issue(s) require immediate attention`
                            : alarmsData.summary?.yellow_count > 0
                            ? `${alarmsData.summary.yellow_count} warning(s) to monitor`
                            : "All systems healthy"}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-1">
                        <span className="w-3 h-3 rounded-full bg-red-500" />
                        <span className="text-white font-bold">{alarmsData.summary?.red_count || 0}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <span className="w-3 h-3 rounded-full bg-amber-500" />
                        <span className="text-white font-bold">{alarmsData.summary?.yellow_count || 0}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <span className="w-3 h-3 rounded-full bg-emerald-500" />
                        <span className="text-white font-bold">{alarmsData.summary?.green_count || 0}</span>
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={fetchAlarms}
                        className="text-slate-400 hover:text-white"
                      >
                        <RefreshCw className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Alarms Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {alarmsData.alarms?.map((alarm) => (
                    <Card 
                      key={alarm.id}
                      className={`bg-slate-900/50 border-slate-800 cursor-pointer hover:border-slate-600 transition-colors ${
                        selectedAlarm?.id === alarm.id ? "ring-2 ring-cyan-500" : ""
                      }`}
                      onClick={() => setSelectedAlarm(selectedAlarm?.id === alarm.id ? null : alarm)}
                    >
                      <CardHeader className="pb-2">
                        <div className="flex items-center justify-between">
                          <CardTitle className="text-white text-base flex items-center gap-2">
                            {alarm.status === "red" && <AlertOctagon className="h-5 w-5 text-red-400" />}
                            {alarm.status === "yellow" && <AlertTriangle className="h-5 w-5 text-amber-400" />}
                            {alarm.status === "green" && <CheckCircle className="h-5 w-5 text-emerald-400" />}
                            {alarm.name}
                          </CardTitle>
                          <Badge className={
                            alarm.status === "red" ? "bg-red-500/20 text-red-400 border-red-500/30" :
                            alarm.status === "yellow" ? "bg-amber-500/20 text-amber-400 border-amber-500/30" :
                            "bg-emerald-500/20 text-emerald-400 border-emerald-500/30"
                          }>
                            {alarm.display_value}
                          </Badge>
                        </div>
                        <p className="text-slate-400 text-xs mt-1">{alarm.description}</p>
                      </CardHeader>
                      <CardContent>
                        {/* Thresholds Visual */}
                        <div className="flex items-center gap-2 mb-3 text-xs">
                          <span className="flex items-center gap-1">
                            <span className="w-2 h-2 rounded-full bg-emerald-500" />
                            <span className="text-slate-400">{alarm.thresholds?.green}</span>
                          </span>
                          <span className="flex items-center gap-1">
                            <span className="w-2 h-2 rounded-full bg-amber-500" />
                            <span className="text-slate-400">{alarm.thresholds?.yellow}</span>
                          </span>
                          <span className="flex items-center gap-1">
                            <span className="w-2 h-2 rounded-full bg-red-500" />
                            <span className="text-slate-400">{alarm.thresholds?.red}</span>
                          </span>
                        </div>

                        {/* Expanded Details */}
                        {selectedAlarm?.id === alarm.id && (
                          <div className="space-y-4 pt-3 border-t border-slate-700">
                            {/* Formula */}
                            <div className="bg-slate-800/50 rounded p-2">
                              <p className="text-xs text-slate-400">Formula:</p>
                              <p className="text-sm text-cyan-400 font-mono">{alarm.formula}</p>
                            </div>

                            {/* Components */}
                            {alarm.components && (
                              <div>
                                <p className="text-xs text-slate-400 mb-2">Components:</p>
                                <div className="grid grid-cols-2 gap-2 text-xs">
                                  {Object.entries(alarm.components).filter(([k, v]) => 
                                    typeof v === "number" && !k.includes("breakdown")
                                  ).map(([key, value]) => (
                                    <div key={key} className="flex justify-between bg-slate-800/30 p-2 rounded">
                                      <span className="text-slate-400">{key.replace(/_/g, " ")}</span>
                                      <span className="text-white font-medium">
                                        {typeof value === "number" ? formatCurrencyK(value) : value}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Corrections */}
                            <div>
                              <p className="text-xs text-slate-400 mb-2 flex items-center gap-1">
                                <Wrench className="h-3 w-3" />
                                5 Corrective Actions:
                              </p>
                              <div className="space-y-1.5">
                                {alarm.corrections?.map((correction, i) => (
                                  <div 
                                    key={i}
                                    className="flex items-start gap-2 p-2 bg-slate-800/30 rounded hover:bg-slate-800/50 transition-colors"
                                  >
                                    <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${
                                      i === 0 ? "bg-cyan-500/20 text-cyan-400" : "bg-slate-700 text-slate-400"
                                    }`}>
                                      {i + 1}
                                    </span>
                                    <div>
                                      <p className="text-white text-sm font-medium">{correction.action}</p>
                                      <p className="text-slate-400 text-xs">{correction.detail}</p>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Click hint */}
                        {selectedAlarm?.id !== alarm.id && (
                          <p className="text-xs text-slate-500 text-center mt-2">
                            Click to see details & corrections
                          </p>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>

                {/* Missing Data Advisory */}
                {alarmsData.missing_data?.length > 0 && (
                  <Card className="bg-slate-900/50 border-amber-500/30">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-amber-400 text-base flex items-center gap-2">
                        <Info className="h-5 w-5" />
                        Missing Data for Full Analysis
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-slate-400 text-sm mb-3">
                        Data quality score: <span className="text-white font-bold">{alarmsData.data_quality?.score}%</span>
                      </p>
                      <div className="space-y-2">
                        {alarmsData.missing_data.map((item, i) => (
                          <div key={i} className="flex items-start gap-2 p-2 bg-amber-500/5 rounded border border-amber-500/20">
                            <AlertTriangle className="h-4 w-4 text-amber-400 shrink-0 mt-0.5" />
                            <div>
                              <p className="text-white text-sm font-medium">{item.field}</p>
                              <p className="text-slate-400 text-xs">{item.description}</p>
                              <p className="text-amber-400 text-xs">Impact: {item.impact}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                      
                      <div className="mt-4 p-3 bg-slate-800/50 rounded">
                        <p className="text-sm text-slate-300 font-medium mb-2">To improve alarm accuracy:</p>
                        <ul className="space-y-1 text-xs text-slate-400">
                          {alarmsData.data_quality?.recommendations?.filter(Boolean).map((rec, i) => (
                            <li key={i} className="flex items-center gap-2">
                              <span className="w-1.5 h-1.5 bg-cyan-500 rounded-full" />
                              {rec}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </>
            )}
          </TabsContent>
        </Tabs>
      </main>

      {/* SPA Modal */}
      <Dialog open={showSPAModal} onOpenChange={setShowSPAModal}>
        <DialogContent className="bg-slate-900 border-slate-700 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <FileText className="h-5 w-5 text-purple-400" />
              Special Payment Agreement
            </DialogTitle>
          </DialogHeader>
          
          {selectedVendor && (
            <div className="space-y-4">
              {/* Vendor Info */}
              <div className="bg-slate-800/50 rounded-lg p-3">
                <p className="text-white font-medium">{selectedVendor.name}</p>
                <p className="text-slate-400 text-sm">Total Outstanding: {formatCurrency(selectedVendor.total)}</p>
                {selectedVendor.overdue > 0 && (
                  <p className="text-red-400 text-sm">Overdue: {formatCurrency(selectedVendor.overdue)}</p>
                )}
              </div>

              {/* Arrears Repayment */}
              <div>
                <Label className="text-slate-300">Arrears Repayment Plan</Label>
                <div className="grid grid-cols-2 gap-3 mt-2">
                  <div>
                    <Label className="text-slate-400 text-xs">Total Arrears</Label>
                    <Input 
                      type="number"
                      defaultValue={selectedVendor.overdue || selectedVendor.total}
                      className="bg-slate-800 border-slate-700 text-white mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-400 text-xs">Term (months)</Label>
                    <Input 
                      type="number"
                      defaultValue={12}
                      className="bg-slate-800 border-slate-700 text-white mt-1"
                    />
                  </div>
                </div>
              </div>

              {/* Payment Schedule */}
              <div>
                <Label className="text-slate-300">Payment Schedule</Label>
                <div className="grid grid-cols-2 gap-3 mt-2">
                  <Select defaultValue="monthly">
                    <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      <SelectItem value="weekly" className="text-white">Weekly</SelectItem>
                      <SelectItem value="bi-weekly" className="text-white">Bi-weekly</SelectItem>
                      <SelectItem value="monthly" className="text-white">Monthly</SelectItem>
                    </SelectContent>
                  </Select>
                  <div>
                    <Input 
                      type="number"
                      placeholder="Payment day"
                      defaultValue={15}
                      className="bg-slate-800 border-slate-700 text-white"
                    />
                  </div>
                </div>
              </div>

              {/* Current Invoice Treatment */}
              <div>
                <Label className="text-slate-300">Current Invoice Treatment</Label>
                <Select defaultValue="standard">
                  <SelectTrigger className="bg-slate-800 border-slate-700 text-white mt-2">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    <SelectItem value="standard" className="text-white">Pay on standard terms</SelectItem>
                    <SelectItem value="extended" className="text-white">Pay with X days extension</SelectItem>
                    <SelectItem value="percentage" className="text-white">Pay % immediately + defer rest</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSPAModal(false)} className="border-slate-700">
              Cancel
            </Button>
            <Button 
              className="bg-purple-600 hover:bg-purple-700"
              onClick={() => {
                toast.success("Special Payment Agreement created");
                setShowSPAModal(false);
              }}
            >
              Create Agreement
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
